<template>
  <el-dialog
    :title="'复盘信息'"
    :visible.sync="dialogVisible"
    width="80%"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :modal="false"
    :before-close="handleBeforeClose"
    @close="handleClose"
    :append-to-body="true"
    :destroy-on-close="true"
    :modal-append-to-body="true"
    class="game-replay-dialog"
    custom-class="game-replay-view-dialog"
    >
    <div class="replay-view">
      <!-- 赛事信息 -->
      <div class="header-section">
        <div class="game-info">
          <div class="left-section">
            <div class="game-name">{{ gameInfo.game_name }}</div>
            <div class="game-tags">
              <span class="game-tag level-tag">{{ gameInfo.game_level }}</span>
              <span class="game-tag type-tag">{{ gameInfo.game_type }}</span>
            </div>
          </div>
          
          <!-- 订正/AK印章 -->
          <div v-if="stampType" class="replay-stamp">
            <div class="stamp-content" :class="{ 'ak-stamp': stampType === 'AK' }">
              {{ stampType }}
            </div>
          </div>
          
          <div class="right-section">
            <div class="score-info">
              <div class="left-part">
                <i class="el-icon-trophy"></i>
                <span class="label">得分：</span>
              </div>
              <div class="right-part">
                <span class="highlight-text">{{ gameInfo.total_score }}</span>
                <span class="suffix">/{{ totalMaxScore }}</span>
              </div>
            </div>
            <div class="rank-info">
              <div class="left-part">
                <i class="el-icon-medal"></i>
                <span class="label">排名：</span>
              </div>
              <div class="right-part">
                <span class="highlight-text">{{ gameInfo.rank }}</span>
                <span class="suffix">/{{ gameInfo.parameter_number }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 全局复盘内容 -->
      <div class="global-replay">
        <div class="section-title">
          <i class="el-icon-document"></i>
          整体复盘
        </div>
        <mavon-editor
          v-model="replayData.global_replay"
          :subfield="isEditing"
          :defaultOpen="'preview'"
          :toolbarsFlag="isEditing"
          :editable="isEditing"
          :toolbars="markdownToolbars"
          :placeholder="'请输入整体复盘内容'"
          :boxShadow="false"
          :ishljs="true"
          :codeStyle="'github'"
          class="global-replay-editor"
          @change="adjustEditorHeight($event, 'global')"
          ref="globalEditor"
        />
      </div>

      <!-- 题目列表 -->
      <div class="questions-list">
        <div class="section-title">
          <i class="el-icon-notebook-2"></i>
          题目复盘
        </div>
        <div class="questions-grid">
          <div v-for="question in replayData.questions" 
               :key="question.question_code" 
               class="question-card">
            <div class="question-header">
        <!-- 第一行：题号、题目名称、分数、状态标签 -->
        <div class="question-title">
          <span class="seq" :style="getQuestionLevelStyle(question.question_level)">{{ question.question_seq }}</span>
          <span class="name">{{ question.question_name }}</span>
          <div class="scores">
            <span class="score" :class="getScoreClass(question.question_score)">
              {{ question.question_score }}分
            </span>
            <span class="score-separator">/</span>
            <span class="score" :class="getScoreClass(question.question_score_fix)">
              {{ question.question_score_fix }}分
            </span>
          </div>
        </div>
        
        <!-- 第二行：状态标签、错误类型、知识点状态标签合并 -->
        <div class="question-status-and-errors">
          <template v-if="isEditing">
            <!-- 编辑模式：错误类型选择器 -->
            <el-select 
              v-model="question.error_codes" 
              multiple
              filterable
              size="small" 
              style="width: 200px;"
              placeholder="请选择错误类型"
              :disabled="question.question_score === 100"
              @change="handleErrorCodesChange(question)">
              <el-option 
                v-for="err in allErrors" 
                :key="err.error_code" 
                :label="err.error_name" 
                :value="err.error_code">
                <el-tooltip :content="err.error_note" placement="right" effect="dark">
                  <span>
                    <el-tag :style="getErrorOutlineStyle(err.error_code)" size="mini" style="margin-right: 8px;">
                      <i class="el-icon-warning" style="margin-right: 4px;"></i>{{ err.error_name }}
                    </el-tag>
                  </span>
                </el-tooltip>
              </el-option>
            </el-select>
            <!-- 实时验证提示 -->
            <div v-if="isEditing && question.question_score !== 100 && (!question.error_codes || question.error_codes.length === 0)" 
                 class="error-tip">
              <i class="el-icon-warning"></i>
              <span>请选择错误类型</span>
            </div>
          </template>
          <template v-else>
            <!-- 查看模式：显示所有标签 -->
            <!-- 错误类型标签 -->
            <el-tag
              v-for="errorCode in question.error_codes"
              :key="errorCode"
              :style="getErrorOutlineStyle(errorCode)"
              size="small"
              style="margin-right: 4px;">
              <i class="el-icon-warning" style="margin-right: -2px;"></i>
              {{ getErrorName(errorCode) }}
            </el-tag>
            
            <!-- 符合预期状态标签 -->
            <span class="status-tag success" v-if="question.is_expect === '是'">
              <i class="el-icon-check"></i> 符合预期
            </span>
            <span class="status-tag danger" v-else-if="question.is_expect === '否'">
              <i class="el-icon-close"></i> 不符合预期
            </span>
            
            <!-- 补充知识点状态标签 -->
            <span class="status-tag warning" v-if="question.is_plan === '是'">
              <i class="el-icon-warning"></i> 需要补充知识点
              <span v-if="question.plan_name" class="plan-name">{{ question.plan_name }}</span>
            </span>
          </template>
        </div>
              
        <!-- 第三行：标签 -->
        <div class="question-tags" v-if="question.tags && question.tags.length">
          <el-tag
            v-for="tag in question.tags"
            :key="tag.tag_code"
            :style="{ 
              backgroundColor: getTagColor(tag.tag_code),
              borderColor: getTagColor(tag.tag_code),
              color: 'white'
            }"
            size="small"
            style="margin-right: 4px">
            {{ tag.tag_name }}
          </el-tag>
        </div>
            </div>

            <div class="question-content">
              <div class="options-row" v-if="isEditing">
                <div class="option-item">
                  <span class="label">符合预期：</span>
                  <el-select v-model="question.is_expect" size="small" style="width: 80px;">
                    <el-option label="是" value="是"></el-option>
                    <el-option label="否" value="否"></el-option>
                  </el-select>
                </div>
                <div class="option-item">
                  <span class="label">补充知识点：</span>
                  <el-select 
                    v-model="question.is_plan" 
                    size="small" 
                    style="width: 80px;"
                    @change="(val) => handlePlanChange(question)">
                    <el-option label="是" value="是"></el-option>
                    <el-option label="否" value="否"></el-option>
                  </el-select>
                  <el-tag v-if="question.is_plan === '是' && question.plan_name"
                         size="small"
                         type="danger"
                         style="margin-left: 8px;">
                    {{ question.plan_name }}
                  </el-tag>
                </div>
              </div>

              <mavon-editor
                v-model="question.replay_info"
                :subfield="isEditing"
                :defaultOpen="'preview'"
                :toolbarsFlag="isEditing"
                :editable="isEditing"
                :toolbars="markdownToolbarsSimple"
                :placeholder="'请输入题目复盘内容'"
                :boxShadow="false"
                :ishljs="true"
                :codeStyle="'github'"
                :navigation="false"
                class="question-replay-editor"
                @change="(value) => adjustEditorHeight(value, 'question', question.question_code)"
                :ref="`questionEditor-${question.question_code}`"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div slot="footer" class="dialog-footer">
      <el-button type="info" @click="handleCopy">
        <i class="el-icon-picture-outline"></i>
        导出图片
      </el-button>
      <el-button type="warning" v-if="!isEditing" @click="handleEdit">
        <i class="el-icon-edit"></i>
        编辑
      </el-button>
      <el-button v-if="isEditing" @click="handleCancel">
        <i class="el-icon-close"></i>
        取消
      </el-button>
      <el-button type="danger" @click="handleDelete">
        <i class="el-icon-delete"></i>
        删除
      </el-button>
      <el-button type="success" @click="handleConfirm">
        <i class="el-icon-check"></i>
        {{ isEditing ? '保存' : '确定' }}
      </el-button>
    </div>

    <!-- 内嵌二次确认弹窗（避免 Element UI $msgbox 与 Dialog 的 z-index 累积冲突） -->
    <transition name="confirm-fade">
      <div
        v-if="confirmDialog.visible"
        class="inline-confirm-overlay"
        tabindex="-1"
        @click.self="cancelConfirm">
        <div class="inline-confirm-card" role="dialog" aria-modal="true">
          <div class="inline-confirm-header">
            <i
              :class="[
                'inline-confirm-icon',
                confirmDialog.type === 'delete' ? 'el-icon-delete is-danger' : 'el-icon-warning-outline is-warning'
              ]"></i>
            <span class="inline-confirm-title">{{ confirmDialog.title || '提示' }}</span>
          </div>
          <div class="inline-confirm-body">
            <p class="inline-confirm-message">{{ confirmDialog.message }}</p>
            <div v-if="confirmDialog.type === 'prompt'" class="inline-confirm-input">
              <el-input
                v-model="confirmDialog.inputValue"
                ref="promptInput"
                @keyup.enter.native="confirmAction"
                :class="{ 'is-invalid': !!confirmDialog.inputError }"
                placeholder="请输入内容" />
              <div v-if="confirmDialog.inputError" class="inline-confirm-error">
                {{ confirmDialog.inputError }}
              </div>
            </div>
          </div>
          <div class="inline-confirm-actions">
            <el-button @click="cancelConfirm">取 消</el-button>
            <el-button
              :type="confirmDialog.type === 'delete' ? 'danger' : 'primary'"
              @click="confirmAction">
              {{ confirmDialog.type === 'delete' ? '删 除' : '确 定' }}
            </el-button>
          </div>
        </div>
      </div>
    </transition>
  </el-dialog>
</template>

<script>
import { getGameReplay, updateGameReplay, deleteGameReplay, addGameReplay } from '@/api/game'
import { getErrorCodes } from '@/api/error'
import { QUESTION_LEVELS, getQuestionLevelStyle } from '@/constants/questionLevel'
import { getErrorTagInlineStyle, getErrorOutlineStyle } from '@/constants/errorTypes'
import html2canvas from 'html2canvas'
import { mavonEditor } from 'mavon-editor'
import 'mavon-editor/dist/css/index.css'

export default {
  name: 'GameReplayView',
  components: {
    mavonEditor
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    gameInfo: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      dialogVisible: false,
      isEditing: false,
      allErrors: [], // 错误类型列表
      replayData: {
        global_replay: '',
        questions: []
      },
      // 添加表单验证规则
      rules: {
        'questions.*.error_codes': [
          { 
            validator: (rule, value, callback) => {
              const questionIndex = parseInt(rule.field.split('.')[1])
              const question = this.replayData.questions[questionIndex]
              
              // 如果得分是100分，则不需要选择错误类型
              if (question && question.question_score === 100) {
                callback()
                return
              }
              
              // 如果得分不是100分，则必须选择错误类型
              if (!value || value.length === 0) {
                callback(new Error('请选择错误类型'))
              } else {
                callback()
              }
            }, 
            trigger: 'change' 
          }
        ]
      },
      // Markdown编辑器工具栏配置
      markdownToolbars: {
        bold: true,
        italic: true,
        header: true,
        underline: true,
        strikethrough: true,
        mark: true,
        superscript: true,
        subscript: true,
        quote: true,
        ol: true,
        ul: true,
        link: true,
        imagelink: true,
        code: true,
        table: true,
        fullscreen: true,
        readmodel: true,
        htmlcode: false,
        help: true,
        undo: true,
        redo: true,
        trash: true,
        navigation: false,
        alignleft: true,
        aligncenter: true,
        alignright: true,
        subfield: true,
        preview: true
      },
      // 简化版工具栏（给题目复盘使用）
      markdownToolbarsSimple: {
        bold: true,
        italic: true,
        header: true,
        underline: true,
        strikethrough: true,
        mark: true,
        superscript: true,
        subscript: true,
        quote: true,
        ol: true,
        ul: true,
        link: true,
        code: true,
        table: true,
        undo: true,
        redo: true,
        trash: true,
        preview: true
      },
      // 内嵌二次确认弹窗状态（避免 Element UI $msgbox 与 Dialog 的 z-index 累积冲突）
      confirmDialog: {
        visible: false,
        type: 'confirm',        // 'confirm' | 'delete' | 'prompt'
        title: '提示',
        message: '',
        inputValue: '',
        inputError: '',
        inputPattern: null,
        inputErrorMessage: '',
        resolve: null,
        reject: null
      }
    }
  },
  watch: {
    visible(val) {
      this.dialogVisible = val
      if (val) {
        this.isEditing = false
        this.replayData = {
          global_replay: '',
          questions: []
        }
        // 获取错误类型列表和复盘数据
        this.fetchErrorCodes()
        this.fetchReplayData()
      }
    },
    dialogVisible(val) {
      if (!val) {
        this.$emit('update:visible', false)
      }
    },
    // 添加对 replayData 的监听，当数据变化时调整所有编辑器高度
    'replayData.global_replay': {
      handler(val) {
        this.$nextTick(() => {
          this.adjustEditorHeight(val, 'global')
        })
      }
    },
    'replayData.questions': {
      handler(val) {
        if (val && val.length > 0) {
          this.$nextTick(() => {
            this.initAllEditorsHeight()
          })
        }
      },
      deep: true
    },
    // 添加对编辑状态的监听，进入编辑模式时重新调整编辑器高度
    isEditing(val) {
      if (val) {
        // 当进入编辑模式时，延迟一点时间再调整所有编辑器高度
        // 确保编辑界面已完全渲染
        setTimeout(() => {
          this.initAllEditorsHeight()
        }, 200)
      }
    }
  },
  methods: {
    getErrorTagInlineStyle,
    getErrorOutlineStyle,
    async fetchReplayData() {
      try {
        const response = await getGameReplay(this.gameInfo.game_code)
        console.log('复盘数据原始响应:', response)
        
        if (response.code === 200) {
          const data = response.data
          if (data.questions && data.questions.length > 0) {
            // 已有复盘数据
            this.replayData = data
            this.isEditing = false
          } else {
            // 没有复盘数据，使用默认值
            this.isEditing = true
            this.replayData = {
              global_replay: '',
              questions: this.gameInfo.questions.map(q => ({
                question_code: q.question_code,
                question_seq: q.question_seq,
                question_name: q.question_name,
                question_score: q.question_score,
                question_score_fix: q.question_score_fix,
                tags: q.tags || [],
                error_codes: q.error_codes || [], // 添加错误类型字段
                is_expect: '否',
                is_plan: '否',
                replay_info: '',
                plan_name: ''
              }))
            }
          }
          console.log('最终的复盘数据:', this.replayData)
          
          // 添加：数据加载完成后，初始化所有编辑器高度
          this.$nextTick(() => {
            this.initAllEditorsHeight()
          })
        }
      } catch (error) {
        console.error('获取复盘数据失败:', error)
        this.$message.error('获取复盘数据失败')
        // 如果获取数据失败，使用默认值
        this.isEditing = true
        this.replayData = {
          global_replay: '',
          questions: this.gameInfo.questions.map(q => ({
            question_code: q.question_code,
            question_seq: q.question_seq,
            question_name: q.question_name,
            question_score: q.question_score,
            question_score_fix: q.question_score_fix,
            tags: q.tags || [],
            error_codes: q.error_codes || [], // 添加错误类型字段
            is_expect: '否',
            is_plan: '否',
            replay_info: '',
            plan_name: ''
          }))
        }
      }
    },
    async fetchErrorCodes() {
      try {
        const res = await getErrorCodes()
        if (res.code === 200) {
          this.allErrors = res.data || []
        }
      } catch (error) {
        console.error('获取错误类型列表失败:', error)
      }
    },
    async handleBeforeClose(done) {
      if (this.isEditing) {
        try {
          await this.showConfirm({
            type: 'confirm',
            title: '提示',
            message: '有未保存的内容，确认关闭吗？'
          })
          this.isEditing = false
          this.dialogVisible = false
          this.$emit('update:visible', false)
          done()
        } catch (e) {
          // 用户取消 - 不调用 done() 保持弹窗打开
        }
      } else {
        this.dialogVisible = false
        this.$emit('update:visible', false)
        done()
      }
    },
    async handleClose() {
      // @close 在弹窗完全关闭后触发（@close 之前一般会被 handleBeforeClose 拦截，
      // 但当 visible 由外部直接置为 false、或 destroy-on-close 销毁内容时仍会进入此分支）。
      // 与原 $confirm 行为保持一致：若仍在编辑状态则提示用户。
      // 注意：此时 el-dialog 子内容已销毁，内嵌确认层不会渲染；Promise 会保持 pending
      // 直到下一次内容重建（再次 open）或组件销毁（beforeDestroy 中 reject）。
      if (this.isEditing) {
        try {
          await this.showConfirm({
            type: 'confirm',
            title: '提示',
            message: '有未保存的内容，确认关闭吗？'
          })
          this.isEditing = false
        } catch (e) {
          // 用户取消 / 异常
        }
      }
      this.dialogVisible = false
      this.$emit('update:visible', false)
    },
    async handleCopy() {
      try {
        const element = document.querySelector('.replay-view')
        // 修改处理过程，适应 mavon-editor 的结构
        const markdownContent = element.querySelectorAll('.v-note-wrapper')
        
        // 在导出前先强制调整所有编辑器的高度，确保内容完全显示
        this.initAllEditorsHeight()
        
        // 临时修改样式以便正确导出
        markdownContent.forEach(wrapper => {
          // 处理 Markdown 编辑器中的内容，确保只显示预览部分
          const editWrapper = wrapper.querySelector('.v-note-edit-wrapper')
          const showContent = wrapper.querySelector('.v-note-show-content')
          // 暂时隐藏编辑区域，只保留预览区域
          if (editWrapper) {
            editWrapper.style.display = 'none'
          }
          if (showContent) {
            showContent.style.overflow = 'visible'
            showContent.style.height = 'auto'
            // 确保显示全部内容
            showContent.style.maxHeight = 'none'
          }
          // 隐藏工具栏
          const toolbar = wrapper.querySelector('.v-note-op')
          if (toolbar) {
            toolbar.style.display = 'none'
          }
          
          // 确保 wrapper 的高度能包含所有内容
          const notePanel = wrapper.querySelector('.v-note-panel')
          if (notePanel) {
            notePanel.style.height = 'auto'
            notePanel.style.maxHeight = 'none'
          }
          
          wrapper.style.height = 'auto'
          wrapper.style.maxHeight = 'none'
        })
        
        // 强制等待一下，确保样式已经应用
        await new Promise(resolve => setTimeout(resolve, 200))
        
        const canvas = await html2canvas(element, {
          useCORS: true,
          scale: 2,  // 提高清晰度
          scrollY: 0,
          height: element.scrollHeight,
          windowHeight: element.scrollHeight,
          onclone: (clonedDoc) => {
            // 优化克隆的 DOM 结构
            const clonedMarkdownContent = clonedDoc.querySelectorAll('.v-note-wrapper')
            clonedMarkdownContent.forEach(wrapper => {
              const editWrapper = wrapper.querySelector('.v-note-edit-wrapper')
              const showContent = wrapper.querySelector('.v-note-show-content')
              // 隐藏编辑区域
              if (editWrapper) {
                editWrapper.style.display = 'none'
              }
              // 确保预览区域可见且显示全部内容
              if (showContent) {
                showContent.style.overflow = 'visible'
                showContent.style.height = 'auto'
                showContent.style.maxHeight = 'none'
              }
              // 隐藏工具栏
              const toolbar = wrapper.querySelector('.v-note-op')
              if (toolbar) {
                toolbar.style.display = 'none'
              }
              
              // 确保 wrapper 高度自适应
              const notePanel = wrapper.querySelector('.v-note-panel')
              if (notePanel) {
                notePanel.style.height = 'auto'
                notePanel.style.maxHeight = 'none'
              }
              
              wrapper.style.height = 'auto'
              wrapper.style.maxHeight = 'none'
            })
          }
        })
        
        // 恢复原始样式
        markdownContent.forEach(wrapper => {
          const editWrapper = wrapper.querySelector('.v-note-edit-wrapper')
          const showContent = wrapper.querySelector('.v-note-show-content')
          const toolbar = wrapper.querySelector('.v-note-op')
          const notePanel = wrapper.querySelector('.v-note-panel')
          
          if (editWrapper && this.isEditing) {
            editWrapper.style.display = ''
          }
          if (showContent) {
            showContent.style.overflow = ''
            showContent.style.height = ''
            showContent.style.maxHeight = ''
          }
          if (toolbar && this.isEditing) {
            toolbar.style.display = ''
          }
          if (notePanel) {
            notePanel.style.height = ''
            notePanel.style.maxHeight = ''
          }
          
          wrapper.style.height = ''
          wrapper.style.maxHeight = ''
        })
        
        // 重新调整高度
        this.initAllEditorsHeight()
        
        const link = document.createElement('a')
        link.download = `复盘_${this.gameInfo.game_name}_${new Date().toLocaleDateString()}.png`
        link.href = canvas.toDataURL()
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        
        this.$message.success('导出成功，请查看下载的图片')
      } catch (error) {
        console.error('导出失败:', error)
        this.$message.error('导出失败')
      }
    },
    handleEdit() {
      this.isEditing = true
      // 延迟执行高度调整，确保编辑模式完全渲染
      this.$nextTick(() => {
        // 确保所有编辑器都已经渲染完成
        setTimeout(() => {
          this.initAllEditorsHeight()
          
          // 强制所有编辑器重新计算尺寸
          if (this.$refs.globalEditor && this.$refs.globalEditor.resize) {
            this.$refs.globalEditor.resize()
          }
          
          // 处理每个问题的编辑器
          this.replayData.questions.forEach(question => {
            const editor = this.$refs[`questionEditor-${question.question_code}`]
            if (editor && editor.resize) {
              editor.resize()
            }
          })
        }, 200)
      })
    },
    async handleDelete() {
      try {
        await this.showConfirm({
          type: 'delete',
          title: '提示',
          message: '确定要删除这条复盘记录吗？'
        })
        const response = await deleteGameReplay(this.gameInfo.game_code)
        if (response.code === 200) {
          this.$message.success('删除成功')
          this.dialogVisible = false
          this.$emit('refresh')
        }
      } catch (e) {
        if (e && e !== 'cancel') {
          this.$message.error('删除失败')
        }
      }
    },
    async handleConfirm() {
      if (this.isEditing) {
        try {
          console.log('开始提交复盘数据...')
          console.log('当前 replayData:', JSON.stringify(this.replayData))
          
          // 验证错误类型选择
          const validationErrors = this.validateErrorCodes()
          
          if (validationErrors.length > 0) {
            this.$message.error('请完善以下信息：\n' + validationErrors.join('\n'))
            return
          }
          
          // 收集表单数据
          const questions = this.replayData.questions.map(question => {
            console.log('处理问题数据:', question)
            // 确保选择了值
            if (!question.is_expect) question.is_expect = '否'
            if (!question.is_plan) question.is_plan = '否'
            
            return {
              question_code: question.question_code,
              is_expect: question.is_expect,
              is_plan: question.is_plan,
              replay_info: question.replay_info || '',
              plan_name: question.plan_name || '',
              error_codes: question.error_codes || []
            }
          })

          const submitData = {
            game_code: this.gameInfo.game_code,
            global_replay: this.replayData.global_replay || '',
            questions: questions
          }

          console.log('准备提交的数据结构:', JSON.stringify(submitData))
          console.log('全局复盘内容长度:', (submitData.global_replay || '').length)
          console.log('全局复盘内容:', submitData.global_replay)
          
          // 直接使用 addGameReplay 创建新复盘
          const response = await addGameReplay(submitData)
          console.log('服务器响应:', response)

          if (response.code === 200) {
            this.$message.success('保存成功')
            this.isEditing = false
            // 重新获取数据以保显示最新状态
            await this.fetchReplayData()
          }
        } catch (error) {
          console.error('操作失败:', error)
          this.$message.error('操作失败')
        }
      } else {
        this.dialogVisible = false
      }
    },
    async handlePlanChange(question) {
      if (question.is_plan === '是') {
        try {
          const { value } = await this.showConfirm({
            type: 'prompt',
            title: '提示',
            message: '请输入学习计划名称',
            inputPattern: /.+/,
            inputErrorMessage: '学习计划名称不能为空'
          })
          question.plan_name = value
        } catch (e) {
          // 用户取消 - 恢复 is_plan 为 '否'
          question.is_plan = '否'
        }
      } else {
        question.plan_name = ''
      }
    },
    async checkHasReplayData() {
      try {
        const response = await getGameReplay(this.gameInfo.game_code);
        return response.code === 200 && response.data && 
               (response.data.global_replay || 
                (response.data.questions && response.data.questions.length > 0));
      } catch (error) {
        console.error('检查复盘数据失败:', error);
        return false;
      }
    },
    getScoreClass(score) {
      if (score === 100) return 'score-perfect'
      if (score >= 60) return 'score-good'
      return 'score-bad'
    },
    async handleCancel() {
      try {
        await this.showConfirm({
          type: 'confirm',
          title: '提示',
          message: '确定要放弃编辑吗？'
        })
        this.isEditing = false
        // 重新获取数据恢复原始状态
        this.fetchReplayData()
      } catch (e) {
        // 用户取消
      }
    },
    getQuestionLevelStyle,
    getQuestionLevelLabel(level) {
      const levelConfig = QUESTION_LEVELS.find(item => item.value === level) || QUESTION_LEVELS[0]
      return levelConfig.label
    },
    getTagColor(tagCode) {
      // 使用与其他组件相同的颜色映射
      const colors = {
        'tag-color-1': '#409EFF',  // Element UI 主蓝色
        'tag-color-2': '#67C23A',  // Element UI 成功绿色
        'tag-color-3': '#E6A23C',  // Element UI 警告黄色
        'tag-color-4': '#F56C6C',  // Element UI 危险红色
        'tag-color-5': '#8E44AD',  // 深紫色
        'tag-color-6': '#16A085',  // 深青色
        'tag-color-7': '#D35400',  // 深橙色
        'tag-color-8': '#2980B9',  // 深蓝色
        'tag-color-9': '#27AE60',  // 深绿色
        'tag-color-10': '#C0392B'  // 深红色
      }
      
      // 使用与其他组件相同的索引生成方式
      const colorClasses = [
        'tag-color-1', 'tag-color-2', 'tag-color-3', 'tag-color-4', 'tag-color-5',
        'tag-color-6', 'tag-color-7', 'tag-color-8', 'tag-color-9', 'tag-color-10'
      ]
      const index = parseInt(tagCode.split('_')[1], 36) % colorClasses.length
      return colors[colorClasses[index]]
    },
    getErrorName(errorCode) {
      const error = this.allErrors.find(err => err.error_code === errorCode)
      return error ? error.error_name : errorCode
    },
    // 验证错误类型选择
    validateErrorCodes() {
      const errors = []
      this.replayData.questions.forEach((question, index) => {
        // 如果得分不是100分，则必须选择错误类型
        if (question.question_score !== 100) {
          if (!question.error_codes || question.error_codes.length === 0) {
            errors.push(`第${question.question_seq}题（${question.question_name}）必须选择错误类型`)
          }
        }
      })
      return errors
    },
    // 实时验证错误类型（在编辑过程中调用）
    validateErrorCodesRealtime(question) {
      if (question.question_score !== 100) {
        if (!question.error_codes || question.error_codes.length === 0) {
          return `第${question.question_seq}题必须选择错误类型`
        }
      }
      return null
    },
    // 处理错误类型选择变化
    handleErrorCodesChange(question) {
      // 可以在这里添加额外的逻辑，比如实时验证提示
      this.$forceUpdate() // 强制更新视图以显示/隐藏验证提示
    },
    // 修改高度调整方法
    adjustEditorHeight(value, type, questionCode) {
      this.$nextTick(() => {
        // 获取对应的编辑器实例
        let editorRef = null
        
        if (type === 'global') {
          editorRef = this.$refs.globalEditor
        } else {
          editorRef = this.$refs[`questionEditor-${questionCode}`]
        }
        
        if (!editorRef) return
        
        // 找到编辑器的关键元素
        const wrapper = editorRef.$el
        if (!wrapper) return

        // 设置合理的最小高度（全局复盘和问题复盘不同）
        const minHeight = '50px'  // 修改为50px
        
        // 根据内容计算高度
        const contentShow = wrapper.querySelector('.v-note-show-content')
        const contentEdit = wrapper.querySelector('.v-note-edit .auto-textarea-input')
        const lineCount = value ? value.split('\n').length : 0
        
        // 计算内容高度（每行约20px，加上一些额外空间）
        let contentHeight = Math.max(lineCount * 24 + 50, parseInt(minHeight))
        if (contentShow && contentShow.scrollHeight > contentHeight) {
          // 如果内容高度大于计算高度，使用实际内容高度，不添加额外空间
          contentHeight = contentShow.scrollHeight
        }
        
        // 设置所有相关元素高度
        const targetHeight = contentHeight + 'px'
        
        if (wrapper) {
          const noteWrapper = wrapper.querySelector('.v-note-wrapper')
          const notePanel = wrapper.querySelector('.v-note-panel')
          const noteShow = wrapper.querySelector('.v-note-show')
          const noteEdit = wrapper.querySelector('.v-note-edit')
          
          if (noteWrapper) noteWrapper.style.minHeight = minHeight
          if (notePanel) {
            notePanel.style.height = targetHeight
            notePanel.style.minHeight = minHeight
          }
          if (noteShow) {
            noteShow.style.height = targetHeight
            noteShow.style.minHeight = minHeight
          }
          if (noteEdit) {
            noteEdit.style.height = targetHeight
            noteEdit.style.minHeight = minHeight
          }
          if (contentShow) {
            contentShow.style.minHeight = minHeight
            contentShow.style.height = targetHeight
          }
          if (contentEdit) {
            contentEdit.style.minHeight = minHeight
            contentEdit.style.height = targetHeight
          }
        }
        
        // 强制重新渲染编辑器确保高度生效
        if (editorRef && editorRef.resize) {
          editorRef.resize()
        }
      })
    },
    // 初始化所有编辑器高度
    initAllEditorsHeight() {
      // 调整全局复盘编辑器高度
      if (this.replayData.global_replay) {
        this.adjustEditorHeight(this.replayData.global_replay, 'global')
      }
      
      // 调整所有题目复盘编辑器高度
      if (this.replayData.questions && this.replayData.questions.length > 0) {
        this.replayData.questions.forEach(question => {
          if (question.replay_info) {
            this.adjustEditorHeight(question.replay_info, 'question', question.question_code)
          }
        })
      }
      
      // 在编辑模式下，再次调整所有编辑器高度（确保编辑区和预览区高度匹配）
      if (this.isEditing) {
        // 延迟一点时间，确保编辑界面已完全渲染
        setTimeout(() => {
          this.replayData.questions.forEach(question => {
            if (question.replay_info) {
              this.adjustEditorHeight(question.replay_info, 'question', question.question_code)
            }
          })
          
          if (this.replayData.global_replay) {
            this.adjustEditorHeight(this.replayData.global_replay, 'global')
          }
        }, 50)
      }
    },
    // ========== 内嵌二次确认弹窗相关方法 ==========
    /**
     * 弹出内嵌确认弹窗（替代 this.$confirm / this.$prompt）
     * @param {Object} options
     * @param {string} options.type - 'confirm' | 'delete' | 'prompt'
     * @param {string} options.title - 标题
     * @param {string} options.message - 提示内容
     * @param {string} [options.inputValue] - prompt 模式下的初始输入值
     * @param {RegExp} [options.inputPattern] - prompt 模式下的校验正则
     * @param {string} [options.inputErrorMessage] - prompt 模式下的校验失败提示
     * @returns {Promise} confirm/delete 模式 resolve(), prompt 模式 resolve({ value })
     */
    showConfirm(options = {}) {
      return new Promise((resolve, reject) => {
        this.confirmDialog = {
          visible: true,
          type: options.type || 'confirm',
          title: options.title || '提示',
          message: options.message || '',
          inputValue: options.inputValue || '',
          inputError: '',
          inputPattern: options.inputPattern || null,
          inputErrorMessage: options.inputErrorMessage || '内容不能为空',
          resolve,
          reject
        }
        // 注册全局 Esc 监听（确保任意焦点位置都能响应 Esc）
        document.addEventListener('keydown', this._onConfirmEsc, true)
        // 如果是 prompt 模式，自动聚焦输入框
        this.$nextTick(() => {
          if (this.confirmDialog.type === 'prompt' && this.$refs.promptInput) {
            const inputEl = this.$refs.promptInput.$el
            if (inputEl) {
              const focusable = inputEl.querySelector('input') || inputEl
              if (focusable && focusable.focus) {
                focusable.focus()
              }
            }
          }
        })
      })
    },
    /** 确认动作 - 校验输入并 resolve */
    confirmAction() {
      const cd = this.confirmDialog
      if (!cd || !cd.visible) return

      // prompt 模式做输入校验
      if (cd.type === 'prompt') {
        const value = (cd.inputValue || '').trim()
        if (!value) {
          this.confirmDialog.inputError = cd.inputErrorMessage || '内容不能为空'
          return
        }
        if (cd.inputPattern && !cd.inputPattern.test(value)) {
          this.confirmDialog.inputError = cd.inputErrorMessage || '内容格式不正确'
          return
        }
        const resolve = cd.resolve
        this.closeConfirm()
        resolve({ value: cd.inputValue })
        return
      }

      // confirm / delete 模式直接 resolve
      const resolve = cd.resolve
      this.closeConfirm()
      resolve()
    },
    /** 取消动作 - reject */
    cancelConfirm() {
      if (!this.confirmDialog.visible) return
      const reject = this.confirmDialog.reject
      this.closeConfirm()
      if (reject) reject('cancel')
    },
    /** 关闭确认弹窗（清理状态） */
    closeConfirm() {
      this.confirmDialog = {
        visible: false,
        type: 'confirm',
        title: '提示',
        message: '',
        inputValue: '',
        inputError: '',
        inputPattern: null,
        inputErrorMessage: '',
        resolve: null,
        reject: null
      }
      // 清理全局 Esc 监听
      document.removeEventListener('keydown', this._onConfirmEsc, true)
    },
    /** 全局 Esc 监听回调（带命名空间方便移除） */
    _onConfirmEsc(event) {
      if (event.key === 'Escape' || event.keyCode === 27) {
        event.stopPropagation()
        this.cancelConfirm()
      }
    }
  },
  beforeDestroy() {
    // 清理全局事件监听，避免内存泄漏
    document.removeEventListener('keydown', this._onConfirmEsc, true)
    // 如果有未决的确认弹窗 Promise，应 reject 掉
    if (this.confirmDialog && this.confirmDialog.visible && this.confirmDialog.reject) {
      try { this.confirmDialog.reject('destroy') } catch (e) { /* noop */ }
    }
  },
  computed: {
    totalMaxScore() {
      return this.replayData && this.replayData.questions ? this.replayData.questions.length * 100 : 0
    },
    // 计算总订正分数
    totalScoreFix() {
      if (!this.replayData || !this.replayData.questions) return 0
      return this.replayData.questions.reduce((sum, q) => sum + (q.question_score_fix || 0), 0)
    },
    // 判断是否显示印章以及印章类型
    stampType() {
      // AK：原始分数 = 满分
      if (this.gameInfo.total_score === this.totalMaxScore && this.totalMaxScore > 0) {
        return 'AK'
      }
      // 订正：订正分数 = 满分 且 原始分数 < 满分
      if (this.totalScoreFix === this.totalMaxScore && this.totalMaxScore > 0 && this.gameInfo.total_score < this.totalMaxScore) {
        return '订'
      }
      return null
    }
  }
}
</script>

<style lang="scss" scoped>
.replay-view {
  padding: 20px;
  background-color: #f8fafc;
  border-radius: 12px;

  .header-section {
    background: linear-gradient(to right, #f8fafc, #ffffff);
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 20px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.03);
    border: 1px solid rgba(0,0,0,0.05);
    position: relative; // 为印章定位提供相对位置
    
    .game-info {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      position: relative; // 为印章定位提供相对位置
      
      .left-section {
        flex: 1;
        padding-right: 24px;
        
        .game-name {
          font-size: 20px;
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 8px;
          line-height: 30px;
          letter-spacing: 0.3px;
        }
        
        .game-tags {
          display: flex;
          gap: 8px;
          height: 30px;
          align-items: center;
          
          .game-tag {
            padding: 3px 10px;
            border-radius: 6px;
            font-size: 13px;
            font-weight: 500;
            
            &.level-tag {
              background-color: rgba(24, 144, 255, 0.1);
              color: #1890ff;
            }
            
            &.type-tag {
              background-color: rgba(82, 196, 26, 0.1);
              color: #52c41a;
            }
          }
        }
      }
      
      // 订正/AK印章样式
      .replay-stamp {
        position: absolute;
        left: 70%;
        top: 110%;
        transform: translate(-50%, -50%);
        pointer-events: none; // 印章不影响点击
        z-index: 2;
        
        .stamp-content {
          color: rgba(134, 195, 166, 0.9); // 绿色字体 - 订正
          font-size: 75px; // PC端适当增大
          font-weight: bold;
          transform: rotate(-30deg); // 旋转角度
          position: relative;
          display: flex;
          justify-content: center;
          align-items: center;
          width: 120px; // PC端适当增大
          height: 120px;
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
          
          // 创建双层弧形边框效果
          &:before, &:after {
            content: '';
            position: absolute;
            border-radius: 50%; // 圆形边框
          }
          
          &:before {
            top: -2px;
            left: -2px;
            right: -2px;
            bottom: -2px;
            border: 2px solid rgba(134, 195, 166, 0.9);
            opacity: 0.8;
          }
          
          &:after {
            top: -6px;
            left: -6px;
            right: -6px;
            bottom: -6px;
            border: 1px dashed rgba(134, 195, 166, 0.9);
            opacity: 0.7;
          }
          
          // AK印章的样式
          &.ak-stamp {
            color: rgba(239, 198, 17, 0.95); // 金黄色字体
            
            &:before {
              border: 2px solid rgba(239, 198, 17, 0.95);
              opacity: 0.9;
            }
            
            &:after {
              border: 1px dashed rgba(239, 198, 17, 0.95);
              opacity: 0.8;
            }
          }
        }
      }
      
      .right-section {
        display: flex;
        flex-direction: column;
        gap: 4px;
        min-width: 220px;
        margin: 0;
        padding: 0;
        height: 64px;
        justify-content: center;
        
        .score-info, .rank-info {
          display: flex;
          align-items: center;
          background: #ffffff;
          padding: 3px 12px;
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.04);
          height: 30px;
          width: 200px;
          border: 1px solid rgba(0,0,0,0.03);
          
          .left-part {
            display: flex;
            align-items: center;
            
            i {
              font-size: 16px;
              margin-right: 6px;
              
              &.el-icon-trophy {
                color: #F5A623;
              }
              
              &.el-icon-medal {
                color: #1890FF;
              }
            }

            .label {
              color: #666;
              font-size: 14px;
              font-weight: 500;
            }
          }

          .right-part {
            display: flex;
            align-items: baseline;
            margin-left: auto;
            
            .highlight-text {
              font-size: 38px;
              font-weight: 600;
              color: #1890ff;
              line-height: 1;
            }

            .suffix {
              color: #8c8c8c;
              font-size: 14px;
              margin-left: 3px;
              line-height: 1;
              padding-bottom: 3px;
            }
          }
        }
      }
    }
  }

  .questions-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
    margin-bottom: 16px;
    
    .question-card {
      background: #fff;
      border-radius: 12px;
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.03);
      border: 1px solid rgba(0,0,0,0.05);
      overflow: hidden;
      display: flex;
      flex-direction: column;
      
      .question-header {
        background: linear-gradient(to right, #f8fafc, #ffffff);
        padding: 12px;
        border-bottom: 1px solid #ebeef5;
        min-height: 72px;  /* 改为最小高度，支持多行内容 */
        box-sizing: border-box;
        
        .question-title {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 8px;
          
          .seq {
            font-weight: 600;
            color: white;
            font-size: 12px;
            padding: 2px 8px;
            border-radius: 4px;
            font-weight: 500;
          }
          
          .name {
            font-weight: 600;
            color: #1f2937;
            font-size: 15px;
            flex: 1;
          }
          
          .scores {
            display: flex;
            align-items: center;
            gap: 4px;
            
            .score {
              font-weight: 600;
              font-size: 15px;
              
              &.score-perfect { 
                color: #67C23A;
              }
              &.score-good { 
                color: #409EFF;
              }
              &.score-bad { 
                color: #F56C6C;
              }
            }
            
            .score-separator {
              color: #909399;
              font-weight: 500;
            }
          }
        }
        
        .question-status-and-errors {
          display: flex;
          flex-wrap: wrap;
          gap: 4px;
          margin-bottom: 8px;
          align-items: center;
          
          .el-tag {
            margin: 0;
            border: 1px solid !important; // 确保边框显示
            padding: 2px 8px;
            height: 22px;
            line-height: 18px;
            border-radius: 4px;
          }
          
          .status-tag {
            background-color: #f0f9eb;
            color: #67C23A;
            font-size: 12px;
            padding: 4px 8px;
            border-radius: 4px;
            display: inline-flex;
            align-items: center;
            margin-right: 4px;
            
            i {
              margin-right: 4px;
              font-size: 12px;
            }
            
            &.warning {
              background-color: #fdf6ec;
              color: #E6A23C;
            }
            
            &.danger {
              background-color: #fde2e2;
              color: #F56C6C;
            }
            
            .plan-name {
              background-color: rgba(230, 162, 60, 0.2);
              padding: 0 4px;
              margin-left: 4px;
              border-radius: 2px;
              font-size: 11px;
            }
          }
          
          .error-tip {
            display: flex;
            align-items: center;
            color: #F56C6C;
            font-size: 12px;
            margin-top: 4px;
            width: 100%;
            
            i {
              margin-right: 4px;
              font-size: 14px;
            }
          }
        }
        
        
        .question-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          
          .el-tag {
            margin: 0;
            border: none;
            padding: 2px 8px;
            height: 22px;
            line-height: 18px;
            border-radius: 4px;
          }
        }
      }
      
      .question-content {
        flex: 1;
        display: flex;
        flex-direction: column;
        padding: 0;
        min-height: 100px; // 修改为100px，使内容区域更紧凑
        
        .options-row {
          display: flex;
          gap: 24px;
          margin-bottom: 14px;
          flex-wrap: wrap;
          padding: 12px 12px 0 12px; // 添加内边距（上左右12px，下0）
          
          .option-item {
            display: flex;
            align-items: center;
            
            .label {
              color: #606266;
              margin-right: 4px;
              min-width: 40px;
              font-size: 14px;
            }
            
            .value {
              font-weight: 500;
              min-width: 30px;
              font-size: 14px;
              margin-left: 4px;
              
              &.success { 
                color: #67C23A;
              }
              &.danger { 
                color: #F56C6C;
              }
            }
            
            .error-tags-display {
              display: flex;
              flex-wrap: wrap;
              gap: 4px;
              align-items: center;
              
              .el-tag {
                margin: 0;
              }
              
              .no-error {
                color: #909399;
                font-size: 14px;
              }
            }
          }
          
        }
      }
    }
  }
}

.dialog-footer {
  .el-button {
    padding: 10px 20px;
    border-radius: 6px;
    transition: all 0.3s ease;
    
    i {
      margin-right: 6px;
    }
    
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }
  }
}

.game-replay-dialog {
  :deep(.el-dialog) {
    max-height: 90vh;
    margin-top: -5vh !important;
    display: flex;
    flex-direction: column;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
    
    .el-dialog__header {
      padding: 16px 20px;
      margin: 0;
      background: linear-gradient(to right, #f8fafc, #ffffff);
      border-bottom: 1px solid #ebeef5;
      
      .el-dialog__title {
        font-size: 18px;
        font-weight: 600;
        color: #1f2937;
        letter-spacing: 0.3px;
      }
      
      .el-dialog__headerbtn {
        top: 18px;
        right: 20px;
        
        i {
          font-size: 18px;
          transition: all 0.3s ease;
          
          &:hover {
            transform: rotate(90deg);
          }
        }
      }
    }
    
    .el-dialog__body {
      flex: 1;
      overflow-y: auto;
      padding: 20px;
      margin: 0;
      background-color: #f8fafc;
      
      &::-webkit-scrollbar {
        width: 6px;
      }
      
      &::-webkit-scrollbar-thumb {
        background-color: rgba(0, 0, 0, 0.1);
        border-radius: 3px;
      }
      
      &::-webkit-scrollbar-track {
        background-color: transparent;
      }
    }
    
    .el-dialog__footer {
      padding: 16px 20px;
      margin: 0;
      background: #ffffff;
      border-top: 1px solid #ebeef5;
    }
  }
}

.section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 8px;
  padding-left: 10px;
  border-left: 4px solid #409EFF;
  
  i {
    font-size: 18px;
    color: #409EFF;
    text-shadow: 0 0 8px rgba(64, 158, 255, 0.2);
  }
}

.global-replay {
  margin-bottom: 8px; // 设置底部间距，与题目复盘区域保持间距
  
  .global-replay-editor {
    background: #fff;
    border-radius: 8px;
    transition: all 0.3s ease;
    min-height: 50px !important; // 修改整体复盘最小高度为50px
    
    &:hover {
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
    }
    
    :deep(.v-note-panel) {
      min-height: 50px !important; // 修改为50px
      background: #fff !important; // 设置白色背景
    }
    
    :deep(.v-note-edit.divarea-wrapper) {
      min-height: 50px !important; // 修改为50px
      background: #fff !important; // 设置白色背景
      padding: 0 !important; // 编辑模式：移除外层padding
    }
    
    :deep(.v-note-show) {
      min-height: 50px !important; // 修改为50px
      background: #fff !important; // 设置白色背景
      padding: 12px !important; // 显示模式：保持12px（不修改）
    }
    
    :deep(.v-note-show-content) {
      min-height: 50px !important; // 修改为50px
      background: #fff !important; // 设置白色背景
      padding: 12px !important; // 显示模式：保持12px（不修改）
      
      p {
        margin-bottom: 8px !important; // 设置p标签底部margin为8px
      }
    }
    
    :deep(.auto-textarea-input) {
      min-height: 50px !important; // 修改为50px
      background: #fff !important; // 设置白色背景
      padding: 12px !important; // 编辑模式：只在最内层保留12px
      
      p {
        margin-bottom: 8px !important; // 设置p标签底部margin为8px
      }
    }
    
    :deep(.v-note-edit-wrapper) {
      padding: 0 !important; // 编辑模式：移除外层padding
    }
    
    :deep(.v-note-show-wrapper) {
      padding: 12px !important; // 显示模式：保持12px（不修改）
    }
    
    // 移除编辑模式中间层的padding
    :deep(.v-note-edit) {
      .content-input-wrapper,
      .auto-textarea-wrapper,
      .auto-textarea-block {
        padding: 0 !important;
      }
    }
    
    // 确保工具栏在编辑模式下有足够的空间
    :deep(.v-note-op) {
      padding: 8px 10px;
      min-height: 42px;
      background: #fff !important; // 设置白色背景
    }
  }
}

.question-replay-editor {
  background: #fff;
  border-radius: 8px;
  transition: height 0.3s ease;
  flex: 1;
  
  // 工具栏布局设置 - 一行显示（仅在工具栏显示时生效）
  :deep(.v-note-op) {
    flex-wrap: nowrap !important;
    
    .v-left-item {
      flex: 1 !important;
      width: auto !important;
      min-width: 0 !important;
      display: flex !important;
      flex-wrap: wrap !important;
    }
    
    .v-right-item {
      flex: 0 0 auto !important;
      width: auto !important;
      display: flex !important;
      flex-wrap: nowrap !important;
      margin-left: 8px !important;
    }
  }
  
  // 覆盖mavon-editor默认样式，设置题目复盘p标签margin
  :deep(.v-note-wrapper .v-note-panel .v-note-show .v-show-content) {
    p {
      margin: 0 0 8px 0 !important; // 设置题目复盘p标签底部margin为8px
    }
  }
}

// 修改编辑器相关CSS样式
:deep(.v-note-wrapper) {
  z-index: 1 !important;
  border: 1px solid #dcdfe6;
  border-radius: 8px;
  overflow: hidden;
  min-height: 50px !important;  // 修改为50px，使用!important
  height: auto !important;
  
  .v-note-panel {
    min-height: 50px !important;  // 修改为50px，使用!important
    height: auto !important;
  }
  
  .v-note-edit.divarea-wrapper,
  .v-note-show {
    min-height: 50px !important;  // 修改为50px，使用!important
  }
  
  .v-note-edit-wrapper,
  .v-note-show {
    flex: 1 !important;
    min-width: unset !important;
    padding: 12px !important;
    box-sizing: border-box;
    min-height: 50px !important;  // 修改为50px，使用!important
    margin-bottom: 0px !important;
  }
  
  .v-note-show-content {
    min-height: 50px !important;  // 修改为50px，使用!important
  }
  
  // 编辑区域样式调整
  .v-note-edit {
    min-height: 50px !important;  // 修改为50px，使用!important
    
    .auto-textarea-wrapper,
    .auto-textarea-block,
    .auto-textarea-input {
      min-height: 50px !important;  // 修改为50px，使用!important
      line-height: 1.6;
      font-size: 14px;
    }
  }
}

// 添加这些被移除的重要样式
.v-note-navigation-wrapper {
  display: none !important;
}

.v-note-op {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: #fff;
  border-bottom: 1px solid #e4e7ed;
}

// 全局复盘编辑器样式优化
.global-replay-editor {
  :deep(.v-note-wrapper) {
    min-height: 50px !important;  // 修改为50px，使用!important
  }
  
  :deep(.v-note-panel) {
    min-height: 50px !important;  // 修改为50px，使用!important
  }
  
  :deep(.v-note-edit.divarea-wrapper),
  :deep(.v-note-show) {
    min-height: 50px !important;  // 修改为50px，使用!important
  }
  
  :deep(.v-note-show-content) {
    min-height: 50px !important;  // 修改为50px，使用!important
    padding: 12px !important; // 添加内边距
    
    p {
      margin-bottom: 8px !important; // 设置p标签底部margin为8px
    }
  }
  
  // 添加更具体的选择器确保优先级
  :deep(.v-note-edit) {
    min-height: 50px !important;
    
    .auto-textarea-wrapper,
    .auto-textarea-block {
      min-height: 50px !important;
    }
    
    .auto-textarea-input {
      min-height: 50px !important;
      padding: 12px !important; // 添加内边距
    }
  }
  
  // 处理 v-note-show single-show 等额外容器
  :deep(.v-note-show.single-show) {
    min-height: 50px !important;
    height: auto !important;
    max-height: none !important;
    margin-bottom: 0 !important;
    padding: 12px !important; // 恢复padding为12px
    
    p {
      margin-bottom: 8px !important; // 设置p标签底部margin为8px
    }
  }
  
  :deep(.v-note-edit-wrapper) {
    min-height: 50px !important;
  }
  
  // 确保所有可能的容器都设置正确的最小高度
  :deep(.v-note-edit-wrapper .v-note-show),
  :deep(.v-note-edit-wrapper .v-note-edit) {
    min-height: 50px !important;
  }
}

// 题目复盘编辑器样式优化
.question-replay-editor {
  :deep(.v-note-wrapper) {
    min-height: 50px !important;  // 题目复盘也改为50px
  }
  
  :deep(.v-note-panel) {
    min-height: 50px !important;  // 题目复盘也改为50px
  }
  
  :deep(.v-note-edit.divarea-wrapper) {
    min-height: 50px !important;  // 题目复盘也改为50px
    padding: 0 !important;  // 编辑模式：移除外层padding
  }
  
  :deep(.v-note-show) {
    min-height: 50px !important;  // 题目复盘也改为50px
    // 显示模式：不修改padding
  }
  
  :deep(.v-note-show-content) {
    min-height: 50px !important;  // 题目复盘也改为50px
    padding: 12px !important; // 显示模式：保持12px（不修改）
    
    p {
      margin-bottom: 8px !important; // 设置p标签底部margin为8px
    }
  }
  
  :deep(.auto-textarea-input) {
    min-height: 50px !important;  // 题目复盘也改为50px
    padding: 12px !important; // 编辑模式：只在最内层保留12px
    
    p {
      margin-bottom: 8px !important; // 设置p标签底部margin为8px
    }
  }
  
  // 覆盖mavon-editor默认样式，设置题目复盘p标签margin
  :deep(.v-note-wrapper .v-note-panel .v-note-show .v-show-content) {
    p {
      margin: 0 0 8px 0 !important; // 设置题目复盘p标签底部margin为8px
    }
  }
  
  // 添加更具体的选择器确保优先级
  :deep(.v-note-edit) {
    min-height: 50px !important;
    
    .content-input-wrapper,
    .auto-textarea-wrapper,
    .auto-textarea-block {
      padding: 0 !important;  // 移除中间层padding
    }
  }
  
  // 处理 v-note-show single-show 等额外容器
  :deep(.v-note-show.single-show) {
    min-height: 50px !important;
    height: auto !important;
    max-height: none !important;
    margin-bottom: 0px !important;
    padding: 12px !important; // 恢复padding为12px
    
    p {
      margin-bottom: 8px !important; // 设置p标签底部margin为8px
    }
  }
  
  :deep(.v-note-edit-wrapper) {
    min-height: 50px !important;
  }
  
  // 确保所有可能的容器都设置正确的最小高度
  :deep(.v-note-edit-wrapper .v-note-show),
  :deep(.v-note-edit-wrapper .v-note-edit) {
    min-height: 50px !important;
  }
}

// ========== 内嵌二次确认弹窗样式（避免与 Element UI $msgbox 的 z-index 冲突） ==========
.inline-confirm-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  // z-index 设为 9999，超过 v-modal(2000) 和 el-message-box(2010)，
  // 避免父级 dialog 多次 open 累积 z-index 后被遮罩挡住
  z-index: 9999;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  // 居中：考虑 App.vue 中 .el-message-box__wrapper:after { height: 75% } 偏移
  // 父级 .el-dialog 默认有 5vh 顶部间距 + -5vh margin-top，叠加后约视口中央偏上
  padding: 0;
  animation: confirm-fade-in 0.2s ease-out;
  outline: none;
}

.inline-confirm-card {
  width: 420px;
  max-width: calc(100vw - 40px);
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.inline-confirm-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 20px;
  border-bottom: 1px solid #ebeef5;
  background: #fafafa;

  .inline-confirm-icon {
    font-size: 20px;

    &.is-warning {
      color: #E6A23C;
    }

    &.is-danger {
      color: #F56C6C;
    }
  }

  .inline-confirm-title {
    font-size: 15px;
    font-weight: 600;
    color: #303133;
  }
}

.inline-confirm-body {
  padding: 20px;

  .inline-confirm-message {
    margin: 0;
    font-size: 14px;
    line-height: 1.6;
    color: #606266;
  }

  .inline-confirm-input {
    margin-top: 16px;

    // 当校验失败时，input 边框变红
    :deep(.el-input.is-invalid .el-input__wrapper),
    :deep(.el-input.is-invalid .el-input__inner) {
      border-color: #F56C6C;
      box-shadow: 0 0 0 1px #F56C6C inset;
    }
  }

  .inline-confirm-error {
    margin-top: 6px;
    font-size: 12px;
    color: #F56C6C;
    line-height: 1.4;
  }
}

.inline-confirm-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 20px 16px;
  background: #fff;

  // 让两个按钮尺寸统一、紧凑
  :deep(.el-button) {
    min-width: 76px;
    height: 32px;
    padding: 0 16px;
  }
}

// 过渡动画
.confirm-fade-enter-active,
.confirm-fade-leave-active {
  transition: opacity 0.2s ease;

  .inline-confirm-card {
    transition: transform 0.2s ease;
  }
}

.confirm-fade-enter,
.confirm-fade-leave-to {
  opacity: 0;

  .inline-confirm-card {
    transform: scale(0.95);
  }
}

@keyframes confirm-fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

// 移动端适配
@media (max-width: 768px) {
  .inline-confirm-card {
    width: calc(100vw - 32px);
    max-width: 360px;
  }
}
</style>

<style lang="scss">
// 全局样式 - 限制对话框最大宽度
.el-dialog__wrapper .game-replay-view-dialog {
  width: 80% !important;
  max-width: 1300px !important;
  margin: 0 auto !important;
}

.game-replay-view-dialog.el-dialog {
  width: 100% !important;
}
</style>
 